/* ==========================================================================
   Product page redesign — questions and answers (sections/pdp-qa.liquid).

   The section is rendered in full on the server. This adds, per section:
   paging ("Show more questions"), search, sorting, the ask form, votes and
   reports. Questions, votes and reports go to the Product Q&A app through
   the app proxy ({endpoint}/ask, /vote, /report), so the requests stay on
   the shop's own domain.
   ========================================================================== */

(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Storage can be blocked (private windows, strict settings): everything
     here still works without it, it just forgets between visits. */
  const store = {
    get(key, fallback) {
      try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch (error) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* not saved, still works for this page view */
      }
    },
  };

  let memoryVoter = null;
  const voterId = () => {
    let id = store.get('pdpqa:voter', null);
    if (typeof id === 'string' && id) return id;
    id =
      memoryVoter ||
      (window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : Date.now().toString(36) + Math.random().toString(36).slice(2, 12));
    memoryVoter = id;
    store.set('pdpqa:voter', id);
    return id;
  };

  const post = (url, body) =>
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((data) => ({ status: response.status, data: data || {} }))
    );

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  /* A short status line under an answer's buttons: green, or red on error. */
  const say = (el, message, isError) => {
    if (!el) return;
    el.textContent = message;
    el.dataset.tone = isError ? 'error' : 'ok';
  };

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* The bundle or variant chosen in the hero, so the team knows exactly
     what the shopper was looking at. */
  const currentVariantId = () => {
    const input =
      document.querySelector('.pdp-hero form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('.pdp-hero [name="id"]');
    const fromForm = input && parseInt(input.value, 10);
    if (fromForm) return fromForm;
    const fromUrl = parseInt(new URLSearchParams(window.location.search).get('variant') || '', 10);
    return fromUrl || null;
  };

  const init = (root) => {
    if (!root || root.dataset.pdpQaReady) return;
    root.dataset.pdpQaReady = 'true';

    let config = {};
    try {
      config = JSON.parse(root.querySelector('[data-pdp-qa-config]').textContent);
    } catch (error) {
      config = {};
    }
    const endpoint = String(config.endpoint || '/apps/qa').replace(/\/+$/, '');
    const perPage = Math.max(1, parseInt(config.perPage, 10) || 5);
    const genericError =
      config.genericError || "Sorry, that didn't send. Please try again, or call us on 877-242-2792.";

    /* ------------------------------------------------ list, search, sort */

    const list = root.querySelector('[data-pdp-qa-list]');
    const items = list ? Array.from(list.querySelectorAll('[data-pdp-qa-item]')) : [];
    const more = root.querySelector('[data-pdp-qa-more]');
    const moreCount = root.querySelector('[data-pdp-qa-more-count]');
    const noMatch = root.querySelector('[data-pdp-qa-nomatch]');
    const noMatchText = root.querySelector('[data-pdp-qa-nomatch-text]');
    const status = root.querySelector('[data-pdp-qa-status]');
    const search = root.querySelector('[data-pdp-qa-search]');
    const clear = root.querySelector('[data-pdp-qa-clear]');
    const sortSelect = root.querySelector('[data-pdp-qa-sort]');

    items.forEach((item, index) => {
      item.pdpQaOrder = index;
      item.pdpQaText = Array.from(item.querySelectorAll('[data-pdp-qa-text]'))
        .map((el) => el.textContent)
        .join(' ')
        .toLowerCase();
    });

    let shown = perPage;
    let query = '';
    let sort = 'newest';

    const ordered = () => {
      const copy = items.slice();
      if (sort === 'helpful') {
        copy.sort(
          (a, b) =>
            (parseInt(b.dataset.helpful, 10) || 0) - (parseInt(a.dataset.helpful, 10) || 0) ||
            a.pdpQaOrder - b.pdpQaOrder
        );
      } else {
        copy.sort((a, b) => a.pdpQaOrder - b.pdpQaOrder);
      }
      return copy;
    };

    const render = () => {
      if (!list) return;
      const all = ordered();
      all.forEach((item) => list.appendChild(item));

      const terms = query.split(/\s+/).filter(Boolean);
      const matches = terms.length
        ? all.filter((item) => terms.every((term) => item.pdpQaText.includes(term)))
        : all;
      const visible = terms.length ? matches : matches.slice(0, shown);

      all.forEach((item) => {
        item.hidden = !visible.includes(item);
      });
      list.hidden = visible.length === 0;

      if (more) {
        const left = matches.length - visible.length;
        more.hidden = terms.length > 0 || left <= 0;
        if (moreCount) moreCount.textContent = left > 0 ? `(${left} more)` : '';
      }

      if (noMatch) {
        const none = terms.length > 0 && matches.length === 0;
        noMatch.hidden = !none;
        if (none && noMatchText) noMatchText.textContent = `No questions match “${query}”.`;
      }

      if (status) {
        status.textContent = terms.length
          ? matches.length
            ? `${plural(matches.length, 'question matches', 'questions match')} your search.`
            : `No questions match your search.`
          : '';
      }
    };

    if (more) {
      more.addEventListener('click', () => {
        const before = ordered().filter((item) => !item.hidden).length;
        shown += perPage;
        render();
        /* Take keyboard and screen-reader users to the first new question. */
        const next = ordered().filter((item) => !item.hidden)[before];
        const heading = next && next.querySelector('.pdp-qa__question');
        if (heading) heading.focus({ preventScroll: true });
      });
    }

    if (search) {
      let timer = 0;
      const apply = () => {
        query = search.value.trim().toLowerCase();
        if (clear) clear.hidden = search.value === '';
        render();
      };
      search.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(apply, 160);
      });
      search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          window.clearTimeout(timer);
          apply();
        }
        if (event.key === 'Escape' && search.value) {
          search.value = '';
          apply();
        }
      });
      if (clear) {
        clear.addEventListener('click', () => {
          search.value = '';
          apply();
          search.focus();
        });
      }
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        sort = sortSelect.value === 'helpful' ? 'helpful' : 'newest';
        render();
      });
    }

    /* ----------------------------------------------------------- ask form */

    const panel = root.querySelector('[data-pdp-qa-panel]');
    const askButton = root.querySelector('[data-pdp-qa-ask]');
    const form = root.querySelector('[data-pdp-qa-form]');
    const done = root.querySelector('[data-pdp-qa-done]');
    const doneText = root.querySelector('[data-pdp-qa-done-text]');
    const formError = root.querySelector('[data-pdp-qa-form-error]');
    const submit = root.querySelector('[data-pdp-qa-submit]');
    let openedAt = 0;
    let sending = false;

    const field = (name) => (form ? form.elements.namedItem(name) : null);

    const setError = (name, message) => {
      const input = field(name);
      const el = root.querySelector(`[data-pdp-qa-error="${name}"]`);
      if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (el) {
        el.textContent = message || '';
        el.hidden = !message;
      }
    };

    const clearErrors = () => {
      ['question', 'name', 'email'].forEach((name) => setError(name, ''));
      if (formError) {
        formError.textContent = '';
        formError.hidden = true;
      }
    };

    const openPanel = (prefill) => {
      if (!panel) return;
      const wasHidden = panel.hidden;
      panel.hidden = false;
      if (askButton) askButton.setAttribute('aria-expanded', 'true');
      if (!openedAt) openedAt = Date.now();
      const question = field('question');
      if (prefill && question && !question.value.trim()) question.value = prefill;
      if (wasHidden) {
        const top = panel.getBoundingClientRect().top;
        const room = window.innerHeight * 0.25;
        if (top < 0 || top > window.innerHeight - room) {
          panel.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
        }
      }
      const focusTarget = form && !form.hidden ? question : done && done.querySelector('button');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    };

    const closePanel = () => {
      if (!panel) return;
      panel.hidden = true;
      if (askButton) {
        askButton.setAttribute('aria-expanded', 'false');
        askButton.focus({ preventScroll: true });
      }
    };

    if (askButton) {
      askButton.addEventListener('click', () => {
        if (panel && !panel.hidden) closePanel();
        else openPanel('');
      });
    }

    const askQuery = root.querySelector('[data-pdp-qa-ask-query]');
    if (askQuery) {
      askQuery.addEventListener('click', () => {
        const text = search ? search.value.trim() : '';
        /* A search like "charger" reads better as a question to edit. */
        openPanel(text);
      });
    }

    const closeButton = root.querySelector('[data-pdp-qa-close]');
    if (closeButton) closeButton.addEventListener('click', closePanel);

    if (panel) {
      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closePanel();
      });
    }

    const validate = (values) => {
      const errors = {};
      if (!values.question) errors.question = 'Please type your question.';
      else if (values.question.length < 10) errors.question = 'Please add a little more detail, at least 10 characters.';
      else if (values.question.length > 1000) errors.question = 'Please keep your question under 1,000 characters.';
      if (!values.name) errors.name = 'Please tell us what to call you.';
      else if (values.name.length > 40) errors.name = 'Please keep your name under 40 characters.';
      if (!values.email) errors.email = 'Please enter your email, so we can tell you when it’s answered.';
      else if (values.email.length > 254 || !EMAIL.test(values.email)) errors.email = 'Please enter a valid email, like name@example.com.';
      return errors;
    };

    const showErrors = (errors) => {
      let first = null;
      ['question', 'name', 'email'].forEach((name) => {
        if (errors[name]) {
          setError(name, errors[name]);
          if (!first) first = field(name);
        }
      });
      if (first) first.focus();
    };

    const showFormError = (message) => {
      if (!formError) return;
      formError.textContent = message;
      formError.hidden = false;
    };

    const setSending = (on) => {
      sending = on;
      if (!submit) return;
      submit.disabled = on;
      submit.classList.toggle('loading', on);
      submit.setAttribute('aria-busy', on ? 'true' : 'false');
    };

    if (form) {
      ['question', 'name', 'email'].forEach((name) => {
        const input = field(name);
        if (input) input.addEventListener('input', () => setError(name, ''));
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (sending) return;
        clearErrors();

        const values = {
          question: (field('question').value || '').trim(),
          name: (field('name').value || '').trim(),
          email: (field('email').value || '').trim(),
        };
        const errors = validate(values);
        if (Object.keys(errors).length) {
          showErrors(errors);
          return;
        }

        const honeypot = field('website');
        const payload = {
          product_id: config.productId || null,
          variant_id: currentVariantId(),
          name: values.name,
          email: values.email,
          question: values.question,
          website: honeypot ? honeypot.value : '',
          elapsed: openedAt ? Date.now() - openedAt : 0,
        };

        setSending(true);
        post(`${endpoint}/ask`, payload)
          .then(({ status: code, data }) => {
            if (code === 200 && data.ok !== false) {
              if (doneText) {
                /* "Mike R." already ends the sentence: no second full stop. */
                const who = values.name.replace(/[.\s]+$/, '');
                doneText.textContent = `Thanks, ${who}. Your question is with our team, and we’ll email you when it’s answered.`;
              }
              form.hidden = true;
              if (done) {
                done.hidden = false;
                const again = done.querySelector('[data-pdp-qa-again]');
                if (again) again.focus({ preventScroll: true });
              }
              return;
            }
            if (code === 422 && data.errors && typeof data.errors === 'object') {
              const fieldErrors = {};
              ['question', 'name', 'email'].forEach((name) => {
                const message = data.errors[name];
                if (message) fieldErrors[name] = Array.isArray(message) ? message[0] : String(message);
              });
              if (Object.keys(fieldErrors).length) {
                showErrors(fieldErrors);
                return;
              }
            }
            if (code === 429) {
              showFormError(data.message || 'You’ve sent a few questions already. Please try again in a little while.');
              return;
            }
            showFormError(genericError);
          })
          .catch(() => showFormError(genericError))
          .finally(() => setSending(false));
      });
    }

    const again = root.querySelector('[data-pdp-qa-again]');
    if (again && form) {
      again.addEventListener('click', () => {
        const question = field('question');
        if (question) question.value = '';
        clearErrors();
        if (done) done.hidden = true;
        form.hidden = false;
        openedAt = Date.now();
        if (question) question.focus({ preventScroll: true });
      });
    }

    /* -------------------------------------------------- votes and reports */

    const votes = store.get('pdpqa:votes', {});
    const reports = store.get('pdpqa:reports', {});

    items.forEach((item) => {
      const ref = item.dataset.ref;
      if (!ref) return;
      const buttons = Array.from(item.querySelectorAll('[data-pdp-qa-vote]'));
      const feedback = item.querySelector('[data-pdp-qa-feedback]');
      const reportButton = item.querySelector('[data-pdp-qa-report]');
      const confirmRow = item.querySelector('[data-pdp-qa-confirm]');

      const lockVotes = (value) => {
        buttons.forEach((button) => {
          button.disabled = true;
          button.setAttribute('aria-pressed', button.dataset.pdpQaVote === value ? 'true' : 'false');
        });
      };

      const setCounts = (data) => {
        const up = item.querySelector('[data-pdp-qa-count="up"]');
        const down = item.querySelector('[data-pdp-qa-count="down"]');
        if (up && typeof data.helpful === 'number') {
          up.textContent = `(${data.helpful})`;
          item.dataset.helpful = String(data.helpful);
        }
        if (down && typeof data.unhelpful === 'number') down.textContent = `(${data.unhelpful})`;
      };

      if (votes[ref] === 'up' || votes[ref] === 'down') lockVotes(votes[ref]);

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          const value = button.dataset.pdpQaVote;
          buttons.forEach((b) => {
            b.disabled = true;
          });
          post(`${endpoint}/vote`, { ref, value, voter: voterId() })
            .then(({ status: code, data }) => {
              if (code !== 200 || data.ok === false) throw new Error('vote failed');
              setCounts(data);
              lockVotes(value);
              votes[ref] = value;
              store.set('pdpqa:votes', votes);
              say(feedback, 'Thanks for your feedback.');
            })
            .catch(() => {
              buttons.forEach((b) => {
                b.disabled = false;
              });
              say(feedback, 'Sorry, that didn’t save. Please try again.', true);
            });
        });
      });

      if (!reportButton || !confirmRow) return;

      const markReported = () => {
        reportButton.disabled = true;
        reportButton.setAttribute('aria-expanded', 'false');
        confirmRow.hidden = true;
      };

      if (reports[ref]) markReported();

      const closeConfirm = () => {
        confirmRow.hidden = true;
        reportButton.setAttribute('aria-expanded', 'false');
        reportButton.focus({ preventScroll: true });
      };

      reportButton.addEventListener('click', () => {
        if (!confirmRow.hidden) {
          closeConfirm();
          return;
        }
        confirmRow.hidden = false;
        reportButton.setAttribute('aria-expanded', 'true');
        const send = confirmRow.querySelector('[data-pdp-qa-report-send]');
        if (send) send.focus({ preventScroll: true });
      });

      const cancel = confirmRow.querySelector('[data-pdp-qa-report-cancel]');
      if (cancel) cancel.addEventListener('click', closeConfirm);
      confirmRow.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeConfirm();
      });

      const send = confirmRow.querySelector('[data-pdp-qa-report-send]');
      if (send) {
        send.addEventListener('click', () => {
          if (send.disabled) return;
          send.disabled = true;
          post(`${endpoint}/report`, { ref, reason: '', voter: voterId() })
            .then(({ status: code, data }) => {
              if (code !== 200 || data.ok === false) throw new Error('report failed');
              reports[ref] = true;
              store.set('pdpqa:reports', reports);
              markReported();
              say(feedback, 'Thanks, we’ll take a look.');
              reportButton.focus({ preventScroll: true });
            })
            .catch(() => {
              send.disabled = false;
              say(feedback, 'Sorry, that didn’t send. Please try again.', true);
            });
        });
      }
    });

    render();
    root.classList.add('is-ready');
  };

  const start = () => document.querySelectorAll('.pdp-qa[data-section-id]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* Theme editor: a re-rendered section arrives as fresh markup. */
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector('.pdp-qa[data-section-id]'));
  });
})();
